import {
  createSignal,
  createContext,
  useContext,
  createResource,
} from "solid-js";

const I18nContext = createContext();

/**
 * @typedef {object} I18nContextValue
 * @property {() => string} t - extract translations by name
 * @property {() => string} locale - The current locale
 * @property {() => string} setLocale - set a new locale
 * @property {() => boolean} isLoading - Whether translations are being initialized
 */

/**
 * Load translations into memory
 * @param {string} locale The locale to load
 * @returns {object} Translations
 */
async function loadTranslations(locale) {
  // Dynamic import - Vite/webpack will code-split these
  const translations = await import(`@uic/assets/locales/${locale}.json`);
  return translations.default;
}

/**
 * A provider for translations
 * @param {object} props The component props
 * @param {import('solid-js').JSX.Element} props.children The children
 * @returns {import('solid-js').JSX.Element} The provider element
 */
export function I18nProvider(props) {
  const [locale, setLocale] = createSignal("en");
  const [translations] = createResource(locale, loadTranslations);

  const t = (key, params = {}) => {
    const data = translations();
    if (!data) return key;

    const keys = key.split(".");
    let value = data;
    for (const k of keys) {
      value = value?.[k];
    }

    if (!value) return key;

    if (typeof value === "object" && params.count !== undefined) {
      const count = params.count;
      if (count === 0 && value.zero) value = value.zero;
      else if (count === 1 && value.one) value = value.one;
      else value = value.other || value.one || "";
    }

    return value.replace(/\{\{(\w+)\}\}/g, (match, param) => {
      return params[param] ?? match;
    });
  };

  return (
    <I18nContext.Provider
      value={{ t, locale, setLocale, isLoading: () => translations.loading }}
    >
      {props.children}
    </I18nContext.Provider>
  );
}

/**
 * @returns {I18nContextValue} The i18n data
 */
export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}
